import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, Search as SearchIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const STAGES = ["New lead", "Attempted contact", "Connected", "Consultation scheduled", "Lender referred", "Preapproved", "Actively searching", "Showing homes", "Offer stage", "Under contract", "Closed", "Long-term nurture", "Lost"];
const catColor = { Hot: "bg-red-100 text-red-700", Warm: "bg-amber-100 text-amber-700", Nurture: "bg-blue-100 text-blue-700", "Long-term": "bg-gray-100 text-gray-600" };

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filters, setFilters] = useState({ lead_type: "", category: "", status: "", county: "" });
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const [s, l] = await Promise.all([api.get("/admin/stats"), api.get("/admin/leads", { params })]);
    setStats(s.data); setLeads(l.data);
  }, [filters]);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (loading) return <div className="pt-40 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const filtered = leads.filter((l) => !q || `${l.name} ${l.email} ${l.phone}`.toLowerCase().includes(q.toLowerCase()));

  const updateLead = async (id, patch) => {
    const { data } = await api.patch(`/admin/leads/${id}`, patch);
    setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
    if (active?.id === id) setActive(data);
    toast.success("Lead updated");
    load();
  };

  const exportCsv = () => {
    const token = localStorage.getItem("ghc_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/export?token=${token}`, "_blank");
  };

  const stat = [
    ["Total leads", stats?.total], ["New", stats?.new], ["Hot", stats?.hot], ["Warm", stats?.warm],
    ["Buyers", stats?.buyer], ["Sellers", stats?.seller], ["Showings", stats?.showing], ["Consults", stats?.consultation],
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-serif text-4xl text-navy tracking-tight">Lead Dashboard</h1>
            <p className="text-gray-500 text-sm">Welcome, Richard. Manage and follow up on your leads.</p>
          </div>
          <Button onClick={exportCsv} data-testid="export-csv" className="bg-navy text-cream rounded-lg gap-2"><Download size={16} /> Export CSV</Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-8">
          {stat.map(([label, v]) => (
            <div key={label} className="bg-white rounded-xl p-4 border border-gray-200" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="text-2xl font-semibold text-navy">{v ?? 0}</div>
              <div className="text-xs text-gray-500">{label}</div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[180px]">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone" data-testid="admin-search" className="pl-9" />
          </div>
          {[["lead_type", ["", "buyer", "seller", "home_value", "showing", "consultation", "guide", "contact"]],
            ["category", ["", "Hot", "Warm", "Nurture", "Long-term"]],
            ["county", ["", "Fulton", "Gwinnett", "DeKalb", "Walton"]]].map(([k, opts]) => (
            <select key={k} value={filters[k]} data-testid={`admin-filter-${k}`} onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value }))}
              className="border border-gray-200 rounded-lg h-10 px-3 text-sm bg-white capitalize">
              {opts.map((o) => <option key={o} value={o}>{o || `All ${k.replace("_", " ")}`}</option>)}
            </select>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>{["Score", "Name", "Type", "County", "Timeline", "Status", "Created"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} onClick={() => setActive(l)} data-testid={`lead-row-${l.id}`} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catColor[l.category]}`}>{l.score} · {l.category}</span></td>
                  <td className="px-4 py-3 font-medium text-navy">{l.name}<div className="text-gray-400 text-xs">{l.email}</div></td>
                  <td className="px-4 py-3 capitalize">{l.lead_type}</td>
                  <td className="px-4 py-3">{l.county || "—"}</td>
                  <td className="px-4 py-3">{l.timeline || "—"}</td>
                  <td className="px-4 py-3"><span className="text-xs text-gray-600">{l.status}</span></td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{(l.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="7" className="px-4 py-12 text-center text-gray-400">No leads yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">{active.name} — {active.category} ({active.score})</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <Info k="Email" v={active.email} /><Info k="Phone" v={active.phone} />
                <Info k="Type" v={active.lead_type} /><Info k="County" v={active.county} /><Info k="City" v={active.city} />
                <Info k="Price range" v={active.price_range} /><Info k="Timeline" v={active.timeline} />
                <Info k="Preapproved" v={active.preapproved} /><Info k="Contact pref" v={active.contact_preference} />
                <Info k="Source" v={active.source_page} /><Info k="SMS consent" v={active.sms_consent ? "Yes" : "No"} />
                {active.details && Object.keys(active.details).length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="font-medium text-gray-600 mb-1">Details</div>
                    {Object.entries(active.details).map(([k, v]) => <div key={k} className="text-gray-600"><b>{k}:</b> {String(v)}</div>)}
                  </div>
                )}
                <div>
                  <label className="font-medium text-gray-600">Status</label>
                  <select value={active.status} data-testid="lead-status-select" onChange={(e) => updateLead(active.id, { status: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg h-10 px-3 mt-1 bg-white">
                    {STAGES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Follow-up date</label>
                  <Input type="date" defaultValue={active.follow_up_date} data-testid="lead-followup"
                    onBlur={(e) => e.target.value !== active.follow_up_date && updateLead(active.id, { follow_up_date: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <label className="font-medium text-gray-600">Notes</label>
                  <Textarea defaultValue={active.notes} data-testid="lead-notes" placeholder="Add notes…"
                    onBlur={(e) => e.target.value !== active.notes && updateLead(active.id, { notes: e.target.value })} className="mt-1" />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={() => updateLead(active.id, { status: "Connected" })} data-testid="mark-contacted" className="bg-green text-white rounded-lg flex-1">Mark Contacted</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ k, v }) => <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-400">{k}</span><span className="text-navy font-medium text-right capitalize">{v || "—"}</span></div>;
